import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RotateCcw, Search } from "lucide-react";
import {
  symptomSuggestionsApi,
  symptomSuggestionQueryKeys,
  type SymptomSuggestion,
} from "../../shared/api/symptomSuggestions";
import { apiError } from "../../shared/api/client";
import { StatusPill } from "../../shared/ui/StatusPill";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** Cài đặt Gợi ý triệu chứng — danh mục chip triệu chứng cho phần khám bệnh. */
export function SymptomSuggestionsSettingsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SymptomSuggestion | "new" | null>(
    null,
  );
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">(
    "",
  );
  const [bulkError, setBulkError] = useState<string | null>(null);
  const sel = useRowSelection();

  const query = useQuery({
    queryKey: symptomSuggestionQueryKeys.all(),
    queryFn: () => symptomSuggestionsApi.listAll(),
  });

  const rows = useMemo(() => {
    return (query.data ?? [])
      .filter((d) => {
        if (statusFilter === "active" && !d.active) return false;
        if (statusFilter === "inactive" && d.active) return false;
        if (!q.trim()) return true;
        const needle = q.toLowerCase();
        return d.label.toLowerCase().includes(needle);
      })
      .sort(
        (a, b) =>
          a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, "vi"),
      );
  }, [query.data, q, statusFilter]);

  const total = query.data?.length ?? 0;
  const activeCount = (query.data ?? []).filter((d) => d.active).length;

  function refresh() {
    qc.invalidateQueries({ queryKey: symptomSuggestionQueryKeys.all() });
    qc.invalidateQueries({ queryKey: symptomSuggestionQueryKeys.options() });
  }

  const disable = useMutation({
    mutationFn: (id: string) => symptomSuggestionsApi.disable(id),
    onSuccess: refresh,
  });
  const enable = useMutation({
    mutationFn: (id: string) => symptomSuggestionsApi.enable(id),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => symptomSuggestionsApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      refresh();
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });
  const seed = useMutation({
    mutationFn: () => symptomSuggestionsApi.seedDefaults(),
    onSuccess: () => {
      setBulkError(null);
      refresh();
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });
  const [bulkPending, setBulkPending] = useState(false);
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete(
      [...sel.selectedIds],
      symptomSuggestionsApi.remove,
    );
    setBulkPending(false);
    sel.clear();
    refresh();
    setBulkError(
      failed.length ? `${failed.length} triệu chứng không xoá được.` : null,
    );
  }

  return (
    <div className="space-y-4 font-sans">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Gợi ý triệu chứng
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Danh mục triệu chứng gợi ý (VD: Sốt, Ho, Đau họng) — hiển thị ở phần
            “Bệnh nhân tự mô tả” trong màn hình khám bệnh để bác sĩ chọn nhanh
            thay vì gõ tay.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={seed.isPending}
            title="Nạp danh sách triệu chứng mặc định (chỉ khi danh mục trống)"
            onClick={() => {
              if (
                confirm(
                  "Khôi phục danh sách triệu chứng mặc định? Chỉ nạp khi danh mục đang trống.",
                )
              )
                seed.mutate();
            }}
          >
            <RotateCcw />
            {seed.isPending ? "Đang nạp…" : "Khôi phục danh sách mặc định"}
          </Button>
          <Button type="button" onClick={() => setEditing("new")}>
            <Plus />
            Tạo triệu chứng
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">
              Tổng số triệu chứng
            </div>
            <div className="text-2xl font-bold text-foreground">{total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Đang kích hoạt</div>
            <div className="text-2xl font-bold text-foreground">
              {activeCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Đã ngưng</div>
            <div className="text-2xl font-bold text-foreground">
              {total - activeCount}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo tên triệu chứng…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "" | "active" | "inactive")
          }
          aria-label="Lọc theo trạng thái"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 sm:w-52"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="active">Đang kích hoạt</option>
          <option value="inactive">Đã ngưng</option>
        </select>
      </div>

      {query.isError && (
        <p className="text-sm text-destructive">
          Không tải được danh mục triệu chứng.
        </p>
      )}
      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      )}

      {bulkError && <p className="text-sm text-destructive">{bulkError}</p>}
      {!query.isLoading && rows.length > 0 && (
        <>
          <BulkDeleteBar
            count={sel.count}
            noun="triệu chứng"
            pending={bulkPending}
            onClear={sel.clear}
            onDelete={bulkDelete}
          />
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-9 text-center">
                      <input
                        type="checkbox"
                        aria-label="Chọn tất cả"
                        className="h-4 w-4 rounded border-input accent-primary"
                        checked={
                          rows.length > 0 &&
                          rows.every((d) => sel.selectedIds.has(d.id))
                        }
                        ref={(el) => {
                          if (el)
                            el.indeterminate =
                              rows.some((d) => sel.selectedIds.has(d.id)) &&
                              !rows.every((d) => sel.selectedIds.has(d.id));
                        }}
                        onChange={() => {
                          const next = new Set(sel.selectedIds);
                          if (rows.every((d) => next.has(d.id)))
                            rows.forEach((d) => next.delete(d.id));
                          else rows.forEach((d) => next.add(d.id));
                          sel.setSelectedIds(next);
                        }}
                      />
                    </TableHead>
                    <TableHead className="w-20 text-right">Thứ tự</TableHead>
                    <TableHead>Triệu chứng</TableHead>
                    <TableHead>Trạng thái</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          aria-label="Chọn dòng"
                          className="h-4 w-4 rounded border-input accent-primary"
                          checked={sel.selectedIds.has(d.id)}
                          onChange={() => sel.toggle(d.id)}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {d.sortOrder}
                      </TableCell>
                      <TableCell>
                        <strong className="font-semibold text-foreground">
                          {d.label}
                        </strong>
                      </TableCell>
                      <TableCell>
                        {d.active ? (
                          <StatusPill tone="success">Đang kích hoạt</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">Đã ngưng</StatusPill>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(d)}
                          >
                            Sửa
                          </Button>
                          {d.active ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={disable.isPending}
                              onClick={() => disable.mutate(d.id)}
                            >
                              Ngưng
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={enable.isPending}
                              onClick={() => enable.mutate(d.id)}
                            >
                              Kích hoạt
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={remove.isPending}
                            title="Xoá vĩnh viễn triệu chứng"
                            onClick={() => {
                              if (
                                confirm(
                                  `Xoá vĩnh viễn triệu chứng "${d.label}"? Không thể hoàn tác.`,
                                )
                              )
                                remove.mutate(d.id);
                            }}
                          >
                            Xoá
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {!query.isLoading && rows.length === 0 && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {total === 0 ? (
              <>
                Chưa có triệu chứng nào. Bấm <em>+ Tạo triệu chứng</em> hoặc{" "}
                <em>Khôi phục danh sách mặc định</em> ở góc phải để bắt đầu.
              </>
            ) : (
              <>Không có triệu chứng nào khớp bộ lọc.</>
            )}
          </CardContent>
        </Card>
      )}

      {editing && (
        <SymptomFormModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function SymptomFormModal({
  item,
  onClose,
  onSaved,
}: {
  item: SymptomSuggestion | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(item?.label ?? "");
  const [sortOrder, setSortOrder] = useState<number | "">(
    item?.sortOrder ?? "",
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (item) {
        return symptomSuggestionsApi.update(item.id, {
          label: label.trim(),
          sortOrder: sortOrder === "" ? null : Number(sortOrder),
        });
      }
      return symptomSuggestionsApi.create({
        label: label.trim(),
        sortOrder: sortOrder === "" ? null : Number(sortOrder),
      });
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {item ? "Sửa triệu chứng" : "Tạo triệu chứng mới"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-3 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="symptom-label">Tên triệu chứng *</Label>
              <Input
                id="symptom-label"
                required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="VD: Sốt"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="symptom-order">Thứ tự</Label>
              <Input
                id="symptom-order"
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) =>
                  setSortOrder(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                placeholder="100"
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Đóng
            </Button>
            <Button type="submit" disabled={save.isPending || !label.trim()}>
              {save.isPending
                ? "Đang lưu…"
                : item
                  ? "Lưu thay đổi"
                  : "Tạo triệu chứng"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
